import React, { useEffect, useMemo, useState } from 'react';
import {
  Search,
  Loader2,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  Filter,
  Check,
} from 'lucide-react';

import { cn } from '../../../lib/utils';
import { useFetchLeads } from '../../../services/leads';
import { Avatar, AvatarFallback } from '../../ui/avatar';
import {
  initials,
  fullName,
  assigneeName,
  friendlyDate,
  titleCase,
  sourceLabel,
} from './leadHelpers';

const PAGE_SIZE = 15;

const SCROLLBAR =
  '[&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-button]:hidden [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-border hover:[&::-webkit-scrollbar-thumb]:bg-muted-foreground/40';

// Outcome filter chips (empty key = no filter).
const OUTCOME_CHIPS = [
  { key: '', label: 'All' },
  { key: 'working', label: 'Working' },
  { key: 'completed', label: 'Completed' },
  { key: 'dropped', label: 'Dropped' },
  { key: 'withdrawn', label: 'Withdrawn' },
];

// Detail-column filter options (the values shown as pills in the Detail column):
// dropped/deleted plus each claim status. Keys are passed to the backend `detail`
// param verbatim (claim-status keys must match claims.claimStatus values).
const DETAIL_OPTIONS = [
  { key: 'dropped', label: 'Dropped' },
  { key: 'deleted', label: 'Deleted' },
  { key: 'intake', label: 'Intake' },
  { key: 'awaiting medical records', label: 'Awaiting Medical Records' },
  { key: 'in process', label: 'In Process' },
  { key: 'awaiting internal review', label: 'Awaiting Internal Review' },
  { key: 'internal review approved', label: 'Internal Review Approved' },
  { key: 'submitted to va', label: 'Submitted to VA' },
  { key: 'invoiced', label: 'Invoiced' },
  { key: 'payment plan', label: 'Payment Plan' },
  { key: 'paid', label: 'Paid' },
  { key: 'dispute hold', label: 'Dispute Hold' },
  { key: 'dead', label: 'Dead' },
];

// Shared pill shape for the Detail column (claim status + Dropped).
const PILL_BASE =
  'inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium';

// A converted lead's latest claim status, colored by terminal state: completed
// (paid) = green, dead = red, everything else still in flight ("working") = blue.
const claimPillClass = (status) => {
  if (status === 'paid')
    return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300';
  if (status === 'dead')
    return 'bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300';
  return 'bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300';
};

const vetLine = (lead) => {
  const snap = lead.leadMeta?.veteranSnapshot || {};
  return (
    [
      snap.branch,
      snap.currentVaPercentage != null ? `${snap.currentVaPercentage}%` : null,
    ]
      .filter(Boolean)
      .join(' · ') || '—'
  );
};

const sourceOf = (lead) => sourceLabel(lead);

const isDroppedLead = (lead) =>
  !lead.leadConvertedUserId && !!lead.leadDeletedAt;

// The terminal date for a row: when the lead converted (status update) or when
// it was dropped (deleted).
const rowDate = (lead) =>
  isDroppedLead(lead) ? lead.leadDeletedAt : lead.leadStatusUpdatedAt;

// History — a dense, sortable, paginated table of terminal leads: those that
// became clients (with their derived outcome Working / Completed / Withdrawn)
// AND those that were dropped (deleted, with a reason). Built for hundreds of
// rows; clicking one opens a read-only detail modal.
const LeadHistory = ({ heightClass = '' }) => {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [outcome, setOutcome] = useState('');
  const [detail, setDetail] = useState([]); // selected Detail-column values
  const [detailOpen, setDetailOpen] = useState(false);
  const [source, setSource] = useState([]); // selected Source-column labels
  const [sourceOpen, setSourceOpen] = useState(false);
  const [sourceSearch, setSourceSearch] = useState('');
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState('terminalDate');
  const [sortDir, setSortDir] = useState('desc');

  // Detail and the Outcome chips are two slices of the same column; selecting a
  // Detail value clears the (coarser) Outcome chip so they can't contradict
  // (e.g. Outcome=Dropped + Detail=Intake → an impossible, silently-empty set).
  const toggleDetail = (key) => {
    setDetail((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
    setOutcome('');
    setPage(1);
  };

  const toggleSource = (label) => {
    setSource((prev) =>
      prev.includes(label) ? prev.filter((s) => s !== label) : [...prev, label],
    );
    setPage(1);
  };

  // Debounce search.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const params = useMemo(() => {
    const p = {
      history: 'true',
      page,
      limit: PAGE_SIZE,
      sortBy,
      sortDir,
    };
    if (search) p.search = search;
    if (outcome) p.outcome = outcome;
    if (detail.length) p.detail = detail.join(',');
    if (source.length) p.source = JSON.stringify(source);
    return p;
  }, [page, search, outcome, detail, source, sortBy, sortDir]);

  const { data, isLoading } = useFetchLeads(params);
  const rows = useMemo(() => data?.rows || [], [data]);
  const total = data?.total || 0;
  const sourceOptions = useMemo(() => data?.sourceOptions || [], [data]);
  const filteredSourceOptions = useMemo(
    () =>
      sourceOptions.filter((label) =>
        label.toLowerCase().includes(sourceSearch.trim().toLowerCase()),
      ),
    [sourceOptions, sourceSearch],
  );

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const firstShown = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const lastShown = Math.min(page * PAGE_SIZE, total);

  const toggleSort = (key) => {
    if (sortBy === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setSortDir('desc');
    }
    setPage(1);
  };

  // Sortable header cell. `sortKey` omitted → static (non-sortable) header.
  const Th = ({ children, sortKey, className }) => {
    const active = sortKey && sortBy === sortKey;
    const Icon = !sortKey
      ? null
      : active
        ? sortDir === 'asc'
          ? ChevronUp
          : ChevronDown
        : ChevronsUpDown;
    return (
      <th
        className={cn(
          'px-4 py-2.5 text-left text-xs font-medium uppercase tracking-wide text-muted-foreground',
          className,
        )}
      >
        {sortKey ? (
          <button
            type="button"
            onClick={() => toggleSort(sortKey)}
            className={cn(
              'inline-flex items-center gap-1 hover:text-foreground',
              active && 'text-foreground',
            )}
          >
            {children}
            {Icon ? <Icon className="h-3.5 w-3.5" /> : null}
          </button>
        ) : (
          children
        )}
      </th>
    );
  };

  return (
    <div className={cn('flex min-h-0 flex-col pb-4', heightClass)}>
      {/* Chips + search */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap items-center gap-1.5">
          {OUTCOME_CHIPS.map((c) => (
            <button
              key={c.key || 'all'}
              type="button"
              onClick={() => {
                setOutcome(c.key);
                setDetail([]);
                setPage(1);
              }}
              className={cn(
                'rounded-full border px-3 py-1 text-sm font-medium transition-colors',
                outcome === c.key
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-border text-muted-foreground hover:bg-accent hover:text-foreground',
              )}
            >
              {c.label}
            </button>
          ))}
        </div>
        <div className="relative ml-auto w-full sm:w-64">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search history…"
            className="flex h-9 w-full rounded-md border border-input bg-background pl-9 pr-3 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        </div>
      </div>

      {/* Table */}
      <div
        className={cn(
          'min-h-0 flex-1 overflow-auto rounded-xl border border-border bg-card',
          SCROLLBAR,
        )}
      >
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-[1] bg-card">
            <tr className="border-b border-border">
              <Th sortKey="leadLastName">Name</Th>
              <th className="px-4 py-2.5 text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">
                <div className="relative inline-flex items-center gap-1">
                  <span>Detail</span>
                  <button
                    type="button"
                    onClick={() => setDetailOpen((o) => !o)}
                    title="Filter Detail"
                    className={cn(
                      'relative rounded p-0.5 transition-colors hover:text-foreground',
                      detail.length ? 'text-primary' : 'text-muted-foreground',
                    )}
                  >
                    <Filter className="h-3.5 w-3.5" />
                    {detail.length ? (
                      <span className="absolute -right-0.5 -top-0.5 h-1.5 w-1.5 rounded-full bg-primary" />
                    ) : null}
                  </button>
                  {detailOpen ? (
                    <>
                      <button
                        type="button"
                        aria-hidden="true"
                        tabIndex={-1}
                        onMouseDown={(e) => e.preventDefault()}
                        className="fixed inset-0 z-40 cursor-default"
                        onClick={() => setDetailOpen(false)}
                      />
                      <div className="absolute left-0 top-7 z-50 w-60 rounded-xl border border-border bg-card p-1 normal-case shadow-lg">
                        <div className="flex items-center justify-between px-3 py-1.5">
                          <span className="text-xs font-semibold text-foreground">
                            Filter Detail
                          </span>
                          {detail.length ? (
                            <button
                              type="button"
                              onClick={() => {
                                setDetail([]);
                                setPage(1);
                              }}
                              className="text-xs font-medium text-primary hover:underline"
                            >
                              Clear
                            </button>
                          ) : null}
                        </div>
                        <div className="mx-3 my-1 h-px bg-border" />
                        <div className="max-h-72 overflow-y-auto">
                          {DETAIL_OPTIONS.map((opt) => {
                            const checked = detail.includes(opt.key);
                            return (
                              <button
                                key={opt.key}
                                type="button"
                                onClick={() => toggleDetail(opt.key)}
                                className="flex w-full items-center gap-2 rounded-lg px-3 py-1.5 text-left text-sm text-foreground transition-colors hover:bg-accent"
                              >
                                <span
                                  className={cn(
                                    'flex h-4 w-4 shrink-0 items-center justify-center rounded border',
                                    checked
                                      ? 'border-primary bg-primary text-primary-foreground'
                                      : 'border-border',
                                  )}
                                >
                                  {checked ? <Check className="h-3 w-3" /> : null}
                                </span>
                                {opt.label}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </>
                  ) : null}
                </div>
              </th>
              <Th>Branch · VA%</Th>
              <th className="px-4 py-2.5 text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">
                <div className="relative inline-flex items-center gap-1">
                  <span>Source</span>
                  <button
                    type="button"
                    onClick={() => setSourceOpen((o) => !o)}
                    title="Filter Source"
                    className={cn(
                      'relative rounded p-0.5 transition-colors hover:text-foreground',
                      source.length ? 'text-primary' : 'text-muted-foreground',
                    )}
                  >
                    <Filter className="h-3.5 w-3.5" />
                    {source.length ? (
                      <span className="absolute -right-0.5 -top-0.5 h-1.5 w-1.5 rounded-full bg-primary" />
                    ) : null}
                  </button>
                  {sourceOpen ? (
                    <>
                      <button
                        type="button"
                        aria-hidden="true"
                        tabIndex={-1}
                        onMouseDown={(e) => e.preventDefault()}
                        className="fixed inset-0 z-40 cursor-default"
                        onClick={() => setSourceOpen(false)}
                      />
                      <div className="absolute left-0 top-7 z-50 w-64 rounded-xl border border-border bg-card p-1 normal-case shadow-lg">
                        <div className="flex items-center justify-between px-3 py-1.5">
                          <span className="text-xs font-semibold text-foreground">
                            Filter Source
                          </span>
                          {source.length ? (
                            <button
                              type="button"
                              onClick={() => {
                                setSource([]);
                                setPage(1);
                              }}
                              className="text-xs font-medium text-primary hover:underline"
                            >
                              Clear
                            </button>
                          ) : null}
                        </div>
                        <div className="px-2 pb-1.5">
                          <div className="relative">
                            <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                            <input
                              value={sourceSearch}
                              onChange={(e) => setSourceSearch(e.target.value)}
                              placeholder="Search sources…"
                              className="flex h-8 w-full rounded-md border border-input bg-background pl-8 pr-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            />
                          </div>
                        </div>
                        <div className="mx-3 mb-1 h-px bg-border" />
                        <div className="max-h-72 overflow-y-auto">
                          {filteredSourceOptions.map((label) => {
                            const checked = source.includes(label);
                            return (
                              <button
                                key={label}
                                type="button"
                                onClick={() => toggleSource(label)}
                                className="flex w-full items-center gap-2 rounded-lg px-3 py-1.5 text-left text-sm text-foreground transition-colors hover:bg-accent"
                              >
                                <span
                                  className={cn(
                                    'flex h-4 w-4 shrink-0 items-center justify-center rounded border',
                                    checked
                                      ? 'border-primary bg-primary text-primary-foreground'
                                      : 'border-border',
                                  )}
                                >
                                  {checked ? <Check className="h-3 w-3" /> : null}
                                </span>
                                <span className="truncate">{label}</span>
                              </button>
                            );
                          })}
                          {filteredSourceOptions.length === 0 ? (
                            <div className="px-3 py-2 text-xs text-muted-foreground">
                              No sources found.
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </>
                  ) : null}
                </div>
              </th>
              <Th sortKey="terminalDate">Outcome Date</Th>
              <Th>Was Assigned To</Th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td
                  colSpan={6}
                  className="py-12 text-center text-muted-foreground"
                >
                  <Loader2 className="mx-auto h-5 w-5 animate-spin" />
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td
                  colSpan={6}
                  className="py-12 text-center text-sm text-muted-foreground"
                >
                  No leads match this view.
                </td>
              </tr>
            ) : (
              rows.map((lead) => (
                <tr
                  key={lead.leadId}
                  className="border-b border-border last:border-0 hover:bg-accent/40"
                >
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-3">
                      <Avatar className="h-8 w-8 shrink-0">
                        <AvatarFallback className="bg-blue-100 text-xs text-blue-700 dark:bg-blue-500/20 dark:text-blue-300">
                          {initials(lead)}
                        </AvatarFallback>
                      </Avatar>
                      <p className="truncate font-medium text-foreground">
                        {fullName(lead)}
                      </p>
                    </div>
                  </td>
                  <td className="px-4 py-2.5">
                    {isDroppedLead(lead) ? (
                      <span
                        className={cn(
                          PILL_BASE,
                          'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300',
                        )}
                      >
                        Dropped
                      </span>
                    ) : lead.latestClaimStatus ? (
                      <span
                        className={cn(
                          PILL_BASE,
                          claimPillClass(lead.latestClaimStatus),
                        )}
                      >
                        {titleCase(lead.latestClaimStatus)}
                      </span>
                    ) : lead.hadDeletedClaim ? (
                      <span
                        className={cn(
                          PILL_BASE,
                          'bg-slate-100 text-slate-600 dark:bg-slate-500/15 dark:text-slate-300',
                        )}
                      >
                        Deleted
                      </span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-muted-foreground">
                    {vetLine(lead)}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-foreground">
                    {sourceOf(lead)}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-muted-foreground">
                    {friendlyDate(rowDate(lead)) || '—'}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-foreground">
                    {assigneeName(lead)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pager */}
      <div className="mt-3 flex shrink-0 items-center justify-between text-xs text-muted-foreground">
        <span>
          {total === 0
            ? 'No leads'
            : `Showing ${firstShown}–${lastShown} of ${total}`}
        </span>
        <div className="flex items-center gap-1">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="rounded-md border border-border p-1 hover:bg-accent disabled:opacity-40"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            className="rounded-md border border-border p-1 hover:bg-accent disabled:opacity-40"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default LeadHistory;
